import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Package, IndianRupee, ImageIcon, FileText, CheckCircle2 } from "lucide-react";

const CreateListing = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    variety: "Desi",
    quantity_kg: "",
    price_per_kg: "",
    image_url: "",
    description: "",
  });
  const [success, setSuccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user) {
    return (
      <div className="container-gc px-4 py-12">
        <div className="card-gc p-8 text-center">
          <p className="text-stone-700 mb-4">{t.create.login_required}</p>
          <button onClick={() => navigate("/login", { state: { from: "/listings/new" } })} className="btn-primary">
            {t.nav.login}
          </button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.quantity_kg || !form.price_per_kg) {
      setError("Quantity and price are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/listings", {
        variety: form.variety,
        quantity_kg: parseInt(form.quantity_kg, 10),
        price_per_kg: parseFloat(form.price_per_kg),
        image_url: form.image_url || null,
        description: form.description || null,
      });
      setSuccess(true);
      setTimeout(() => navigate("/profile"), 1500);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="create-listing-page" className="container-gc px-4 sm:px-6 py-8 sm:py-12 max-w-2xl">
      <h1 className="text-3xl sm:text-4xl font-bold text-stone-900">{t.create.title}</h1>
      <p className="text-stone-600 mt-1 mb-8">{t.create.subtitle}</p>

      {success ? (
        <div data-testid="create-success" className="card-gc p-8 text-center">
          <CheckCircle2 size={48} className="text-green-600 mx-auto mb-3" />
          <p className="text-lg font-semibold text-stone-900">{t.create.success}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="card-gc p-6 space-y-5">
          {error && (
            <div data-testid="create-error" className="px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-stone-700 mb-2 block">{t.create.variety}</label>
            <div className="grid grid-cols-3 gap-2">
              {["Desi", "G2", "Amleta"].map((v) => (
                <button
                  key={v}
                  data-testid={`variety-${v}-btn`}
                  type="button"
                  onClick={() => setForm({ ...form, variety: v })}
                  className={`h-12 rounded-xl border-2 font-semibold ${
                    form.variety === v
                      ? "border-[#1E4D2B] bg-[#1E4D2B] text-white"
                      : "border-stone-300 bg-white text-stone-700"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.create.quantity}</label>
              <div className="relative">
                <Package size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="quantity-input"
                  type="number"
                  min="1"
                  className="input-gc pl-10"
                  placeholder="500"
                  value={form.quantity_kg}
                  onChange={(e) => setForm({ ...form, quantity_kg: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.create.price}</label>
              <div className="relative">
                <IndianRupee size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  data-testid="price-input"
                  type="number"
                  step="0.01"
                  className="input-gc pl-10"
                  placeholder="150"
                  value={form.price_per_kg}
                  onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.create.image}</label>
            <div className="relative">
              <ImageIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                data-testid="image-input"
                className="input-gc pl-10"
                placeholder="https://..."
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-stone-700 mb-1.5 block">{t.create.description}</label>
            <div className="relative">
              <FileText size={18} className="absolute left-3 top-3.5 text-stone-400" />
              <textarea
                data-testid="description-input"
                className="input-gc pl-10 py-3 !h-auto min-h-[96px]"
                placeholder="Fresh, well-stored garlic from MP..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={4}
              />
            </div>
          </div>

          <button
            data-testid="publish-listing-btn"
            type="submit"
            disabled={busy}
            className="btn-primary w-full !h-12"
          >
            {busy ? t.common.loading : t.create.submit}
          </button>
        </form>
      )}
    </div>
  );
};

export default CreateListing;
